import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { API_BASE_URL, authFetch } from '../config/api';

// ── Types ───────────────────────────────────────────────────────────────

interface OrphanRow {
  path: string;
  title: string;
}

interface BrokenLinkGroup {
  target: string;
  count: number;
  sources: { path: string; title: string; snippet: string }[];
}

interface StaleRow {
  path: string;
  title: string;
  mtime: string;
  age_days: number;
}

interface DuplicatePair {
  a: { path: string; title: string };
  b: { path: string; title: string };
  similarity: number;
}

interface HealthResponse {
  ok: boolean;
  totals: {
    orphans: number;
    broken_links: number;
    stale: number;
    duplicates: number;
  };
  total_issues: number;
  orphans: OrphanRow[];
  broken_links: BrokenLinkGroup[];
  stale: StaleRow[];
  duplicates: DuplicatePair[];
}

// ── Data ────────────────────────────────────────────────────────────────

async function fetchHealth(): Promise<HealthResponse> {
  const res = await authFetch(`${API_BASE_URL}/api/knowledge/health`, {
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/** Build the target path used by the Knowledge Center router. The browse
 *  page reads `?path=<relPath>` out of the URL bar to pre-select a note. */
function browseHref(relPath: string): string {
  return `/knowledge?path=${encodeURIComponent(relPath)}`;
}

// ── Page ────────────────────────────────────────────────────────────────

export default function KnowledgeHealthPage() {
  const query = useQuery<HealthResponse, Error>(['knowledgeHealth'], fetchHealth, {
    staleTime: 60_000,
  });

  return (
    <div className="knowledge-browse-page knowledge-health-page">
      <div className="knowledge-browse-page__header">
        <div>
          <h2 className="knowledge-browse-page__title">Vault health</h2>
          <p className="text-muted">
            Orphan notes, broken wikilinks, stale content, and near-duplicate
            pairs. Read-only — click &ldquo;Open&rdquo; on any entry to jump
            to the note.
          </p>
        </div>
        <div
          className="knowledge-browse-page__stats"
          style={{ display: 'flex', gap: 12, alignItems: 'center' }}
        >
          {query.data && (
            <div className="knowledge-stat">
              <span className="knowledge-stat__value">
                {query.data.total_issues}
              </span>
              <span className="knowledge-stat__label">issues</span>
            </div>
          )}
          <Link to="/knowledge" className="btn">
            ← Back to Knowledge Center
          </Link>
        </div>
      </div>

      {query.isLoading && (
        <div className="loading-state">Scanning vault…</div>
      )}
      {query.isError && (
        <div className="error-state">Error: {query.error.message}</div>
      )}
      {query.data && (
        <div className="health-sections">
          <OrphansSection rows={query.data.orphans} />
          <BrokenLinksSection groups={query.data.broken_links} />
          <StaleSection rows={query.data.stale} />
          <DuplicatesSection pairs={query.data.duplicates} />
        </div>
      )}
    </div>
  );
}

// ── Sections ────────────────────────────────────────────────────────────

function HealthSection({
  title,
  subtitle,
  count,
  children,
  defaultOpen = false,
}: {
  title: string;
  subtitle: string;
  count: number;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  // Auto-open the first non-empty section, but respect an explicit override.
  const [open, setOpen] = useState(defaultOpen || count > 0);
  const empty = count === 0;
  return (
    <section className={`health-section${empty ? ' health-section--empty' : ''}`}>
      <button
        type="button"
        className="health-section__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="health-section__title">{title}</span>
        <span className="health-section__count">{count}</span>
        <span className="health-section__chevron" aria-hidden>
          {open ? '▾' : '▸'}
        </span>
      </button>
      {open && (
        <div className="health-section__body">
          <p className="health-section__subtitle">{subtitle}</p>
          {empty ? (
            <div className="health-empty">Nothing to clean up here. ✨</div>
          ) : (
            children
          )}
        </div>
      )}
    </section>
  );
}

function OrphansSection({ rows }: { rows: OrphanRow[] }) {
  return (
    <HealthSection
      title="Orphan notes"
      subtitle="No inbound or outbound wikilinks. These notes sit alone in the vault."
      count={rows.length}
    >
      <ul className="health-list">
        {rows.map((r) => (
          <li key={r.path} className="health-row">
            <div className="health-row__main">
              <div className="health-row__title">{r.title}</div>
              <div className="health-row__meta">{r.path}</div>
            </div>
            <Link to={browseHref(r.path)} className="health-row__action">
              Open →
            </Link>
          </li>
        ))}
      </ul>
    </HealthSection>
  );
}

function BrokenLinksSection({ groups }: { groups: BrokenLinkGroup[] }) {
  return (
    <HealthSection
      title="Broken wikilinks"
      subtitle="Links pointing to a note that doesn't exist. Grouped by missing target — create the note or fix the spelling in the sources."
      count={groups.length}
    >
      <ul className="health-list">
        {groups.map((g) => (
          <BrokenLinkGroupRow key={g.target} group={g} />
        ))}
      </ul>
    </HealthSection>
  );
}

function BrokenLinkGroupRow({ group }: { group: BrokenLinkGroup }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="health-row health-row--group">
      <div className="health-row__main">
        <div className="health-row__title">
          <code>[[{group.target}]]</code>
        </div>
        <div className="health-row__meta">
          {group.count} note{group.count === 1 ? '' : 's'} reference{group.count === 1 ? 's' : ''} this missing target
        </div>
      </div>
      <button
        type="button"
        className="health-row__action"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? 'Hide sources' : `Show ${group.count} source${group.count === 1 ? '' : 's'}`}
      </button>
      {open && (
        <ul className="health-sublist">
          {group.sources.map((s) => (
            <li key={s.path} className="health-subrow">
              <div className="health-subrow__title">
                <Link to={browseHref(s.path)}>{s.title}</Link>
              </div>
              {s.snippet && (
                <div className="health-subrow__snippet">…{s.snippet}…</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function StaleSection({ rows }: { rows: StaleRow[] }) {
  return (
    <HealthSection
      title="Stale notes"
      subtitle="Hazards and Substances notes not edited in 365+ days. Review for outdated regulatory content."
      count={rows.length}
    >
      <ul className="health-list">
        {rows.map((r) => (
          <li key={r.path} className="health-row">
            <div className="health-row__main">
              <div className="health-row__title">{r.title}</div>
              <div className="health-row__meta">
                Last edited {r.age_days} days ago · {r.path}
              </div>
            </div>
            <Link to={browseHref(r.path)} className="health-row__action">
              Open →
            </Link>
          </li>
        ))}
      </ul>
    </HealthSection>
  );
}

function DuplicatesSection({ pairs }: { pairs: DuplicatePair[] }) {
  return (
    <HealthSection
      title="Near-duplicate notes"
      subtitle="Pairs whose centroid embeddings are ≥ 0.92 cosine similarity. Merge or split to keep the vault crisp."
      count={pairs.length}
    >
      <ul className="health-list">
        {pairs.map((p) => (
          <li key={`${p.a.path}::${p.b.path}`} className="health-row">
            <div className="health-row__main">
              <div className="health-row__title">
                {p.a.title} <span className="text-muted">↔</span> {p.b.title}
              </div>
              <div className="health-row__meta">
                similarity {(p.similarity * 100).toFixed(1)}%
              </div>
            </div>
            <div className="health-row__action health-row__action--pair">
              <Link to={browseHref(p.a.path)} className="health-row__link">
                Open A
              </Link>
              <Link to={browseHref(p.b.path)} className="health-row__link">
                Open B
              </Link>
            </div>
          </li>
        ))}
      </ul>
    </HealthSection>
  );
}
